import {
  ChipField,
  CreateButton,
  Datagrid,
  EditButton,
  FunctionField,
  ListButton,
  ReferenceArrayField,
  ReferenceField,
  ReferenceManyField,
  Show,
  SingleFieldList,
  TextField,
  TopToolbar,
  useDataProvider,
  useNotify,
  useRecordContext,
  useRefresh,
} from 'react-admin';
import { ReactNode, useState } from 'react';
import { useClerk, useUser } from '@clerk/clerk-react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import PersonIcon from '@mui/icons-material/Person';

import { ESTDateField } from 'src/client/components/custom-ra-fields/est-date-field';
import { AddLineItemLabelButton } from 'src/client/components/exclusion-labels/add-line-item-label-button';
import { LineItemExclusionLabelChips } from 'src/client/components/exclusion-labels/exclusion-label-chips';
import { Chip } from 'src/client/components/line-items';
import { PreviewClickButton } from 'src/client/components/clicks-creatives';
import { PreviewFMAButton } from 'src/client/components/fma-creatives';
import { formatTargetingType } from 'src/client/utility/display-formatters';
import { formatCampaignTitle } from '../campaigns/CampaignList';
import {
  formatCurrency,
  formatLineItemTitle,
  formatReviewerOption,
  formatTargetingJson,
  getCurrentUserEmail,
  invoiceStatusChoices,
  lineItemStatusChoices,
  resolveReviewerForCurrentUser,
} from './LineItemCreate';
import { DuplicateLineItemButton } from './DuplicateLineItemButton';
import { LineItemArchiveButton } from './LineItemArchiveButton';

type LineItemRecord = {
  id?: string;
  externalPioId?: string | null;
};

const formatChoice = (choices: { id: string; name: string }[], value?: string | null) =>
  choices.find(choice => choice.id === value)?.name ?? value ?? '-';

const LineItemShowActions = () => (
  <TopToolbar>
    <ListButton />
    <EditButton />
    <DuplicateLineItemButton />
    <LineItemArchiveButton />
  </TopToolbar>
);

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Box minWidth={0}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Box sx={{ '& .MuiTypography-root': { fontSize: 'body2.fontSize' } }}>{children}</Box>
  </Box>
);

const sectionGrid = {
  display: 'grid',
  gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
  gap: 2,
} as const;

const TargetingDetail = ({ label }: { label?: string }) => {
  const record = useRecordContext();
  const referenceArrayProps =
    record?.targetingType === 'TARGET_LIST'
      ? { reference: 'targetLists', source: 'targetListIdsList' }
      : { reference: 'specialties', source: 'specialtyIdsList' };

  return (
    <ReferenceArrayField label={label} {...referenceArrayProps}>
      <SingleFieldList>
        <ChipField source="name" />
      </SingleFieldList>
    </ReferenceArrayField>
  );
};

// MD-5145: native line items have no externalPioId; their own UUID `id` is the
// native line-item key. Fall back to `id` so the create actions render for
// native line items while imported line items keep passing externalPioId.
const AddCreativeButton = ({ resource, label }: { resource: string; label: string }) => {
  const record = useRecordContext<LineItemRecord>();
  const lineItemRefId = record?.externalPioId ?? record?.id;
  if (!lineItemRefId) {
    return null;
  }
  return (
    <CreateButton
      color="primary"
      label={label}
      to={{
        pathname: `/${resource}/create`,
        search: `?placementsLineItemId=${encodeURIComponent(lineItemRefId)}`,
      }}
    />
  );
};

// MD-5145: the creative read fires on placementsLineItemId == externalPioId for
// imported line items. Native line items have no externalPioId, so source from
// the native UUID `id` instead; the data provider accepts a native id directly.
const useCreativeReferenceSource = () => {
  const record = useRecordContext<LineItemRecord>();
  return record?.externalPioId ? 'externalPioId' : 'id';
};

const LineItemTitle = () => {
  const record = useRecordContext<Record<string, any>>();
  if (!record) return null;
  return (
    <CardContent>
      <CardHeader title={formatLineItemTitle(record)} sx={{ px: 0, pt: 0, pb: 1 }} />
    </CardContent>
  );
};

const IdentitySection = () => (
  <CardContent>
    <CardHeader title="Overview" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Campaign">
        <ReferenceField source="campaignId" reference="campaigns" link="show">
          <FunctionField render={formatCampaignTitle} />
        </ReferenceField>
      </Field>
      <Field label="Product">
        <ReferenceField source="productId" reference="products" link="show">
          <TextField source="name" emptyText="-" />
        </ReferenceField>
      </Field>
      <Field label="Line Item Number">
        <TextField source="lineItemNumber" emptyText="-" />
      </Field>
      <Field label="Priority">
        <TextField source="priority" emptyText="-" />
      </Field>
      <Field label="Item Type">
        <TextField source="itemType" emptyText="-" />
      </Field>
      <Field label="Delivery Rate Type">
        <TextField source="deliveryRateType" emptyText="-" />
      </Field>
    </Box>
  </CardContent>
);

const ScheduleSection = () => (
  <CardContent>
    <CardHeader title="Schedule &amp; Status" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Start Date">
        <ESTDateField source="startDate" />
      </Field>
      <Field label="End Date">
        <ESTDateField source="endDate" />
      </Field>
      <Field label="Status">
        <FunctionField
          source="status"
          render={(record: Record<string, any>) => formatChoice(lineItemStatusChoices, record?.status)}
        />
      </Field>
      <Field label="Archived At">
        <TextField source="archivedAt" emptyText="-" />
      </Field>
      <Field label="Specialty">
        <TextField source="specialty" emptyText="-" />
      </Field>
    </Box>
  </CardContent>
);

const FinancialSection = () => (
  <CardContent>
    <CardHeader title="Financials" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Goal">
        <TextField source="goal" emptyText="-" />
      </Field>
      <Field label="Goal Type">
        <TextField source="goalType" emptyText="-" />
      </Field>
      <Field label="Contracted Units">
        <TextField source="contractedUnits" emptyText="-" />
      </Field>
      <Field label="Cost Basis">
        <TextField source="costBasis" emptyText="-" />
      </Field>
      <Field label="Cost Per Unit">
        <FunctionField
          source="costPerUnit"
          render={(record: Record<string, any>) => formatCurrency(record?.costPerUnit)}
        />
      </Field>
      <Field label="Currency Code">
        <TextField source="currencyCode" emptyText="-" />
      </Field>
      <Field label="Invoice Amount">
        <FunctionField
          source="invoiceAmount"
          render={(record: Record<string, any>) => formatCurrency(record?.invoiceAmount)}
        />
      </Field>
      <Field label="Invoice Units">
        <TextField source="invoiceUnits" emptyText="-" />
      </Field>
      <Field label="Invoice Status">
        <FunctionField
          source="invoiceStatus"
          render={(record: Record<string, any>) => formatChoice(invoiceStatusChoices, record?.invoiceStatus)}
        />
      </Field>
      <Field label="Revenue">
        <FunctionField source="revenue" render={(record: Record<string, any>) => formatCurrency(record?.revenue)} />
      </Field>
      <Field label="Revenue Units">
        <TextField source="revenueUnits" emptyText="-" />
      </Field>
    </Box>
  </CardContent>
);

const TargetingSection = () => {
  const record = useRecordContext<Record<string, any>>();
  if (!record?.targeting) return null;
  return (
    <CardContent>
      <CardHeader title="Targeting" sx={{ px: 0, pt: 0, pb: 1 }} />
      <Box
        component="pre"
        sx={{
          bgcolor: 'grey.100',
          borderRadius: 1,
          overflow: 'auto',
          p: 2,
          whiteSpace: 'pre-wrap',
          fontSize: 'body2.fontSize',
        }}
      >
        {formatTargetingJson(record.targeting)}
      </Box>
    </CardContent>
  );
};

// On the line-item Show page the reviewer is read-only once set. When it is
// still unassigned, surface a one-click "Mark me as reviewer" action so a CS
// reviewer can self-assign from the review queue (which row-clicks into Show)
// without hunting for the Edit form. Status never gates review. (M3-188)
type ReviewedByRecord = { id?: string; reviewedBy?: string | null };

export const ReviewedByField = () => {
  const record = useRecordContext<ReviewedByRecord>();
  const dataProvider = useDataProvider();
  const { session } = useClerk();
  const { user } = useUser();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isMarking, setIsMarking] = useState(false);
  const currentUserEmail = getCurrentUserEmail(user);

  if (record?.reviewedBy) {
    return (
      <ReferenceField source="reviewedBy" reference="pioUsers" link={false}>
        <FunctionField render={formatReviewerOption} />
      </ReferenceField>
    );
  }

  const handleMarkCurrentUser = async () => {
    if (!record?.id) return;
    if (!currentUserEmail) {
      notify('Current user email is unavailable', { type: 'warning' });
      return;
    }

    setIsMarking(true);
    try {
      const reviewer = await resolveReviewerForCurrentUser({
        currentUserEmail,
        dataProvider,
        getToken: () => session?.getToken() ?? Promise.resolve(null),
      });

      if (!reviewer?.id) {
        notify('Current user could not be resolved as a reviewer', { type: 'warning' });
        return;
      }

      await dataProvider.update('lineItems', {
        id: record.id,
        data: { reviewedBy: reviewer.id },
        previousData: record,
      });
      notify('Marked you as reviewer');
      refresh();
    } catch (error) {
      notify(error instanceof Error ? error.message : 'Could not mark you as reviewer', { type: 'warning' });
    } finally {
      setIsMarking(false);
    }
  };

  return (
    <Button
      variant="outlined"
      size="small"
      startIcon={<PersonIcon />}
      onClick={handleMarkCurrentUser}
      disabled={isMarking || !currentUserEmail}
    >
      Mark me as reviewer
    </Button>
  );
};

const ReferencesSection = () => (
  <CardContent>
    <CardHeader title="External References" sx={{ px: 0, pt: 0, pb: 1 }} />
    <Box sx={sectionGrid}>
      <Field label="Ad Server">
        <TextField source="adServer" emptyText="-" />
      </Field>
      <Field label="Ad Server ID">
        <TextField source="adServerId" emptyText="-" />
      </Field>
      <Field label="GAM Line Item ID">
        <TextField source="gamLineItemId" emptyText="-" />
      </Field>
      <Field label="External PIO ID">
        <TextField source="externalPioId" emptyText="-" />
      </Field>
      <Field label="Copied From">
        <ReferenceField source="copiedFromLineItemId" reference="lineItems" link="show">
          <FunctionField render={formatLineItemTitle} />
        </ReferenceField>
      </Field>
      <Field label="Reviewed By">
        <ReviewedByField />
      </Field>
    </Box>
  </CardContent>
);

const ExclusionLabelsSection = () => (
  <CardContent>
    <CardHeader title="Exclusion Labels" sx={{ px: 0, pt: 0, pb: 1 }} />
    <FunctionField
      source="id"
      render={(record: Record<string, any>) => (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1 }}>
          <LineItemExclusionLabelChips record={record} deletionEnabled />
          <AddLineItemLabelButton />
        </Box>
      )}
    />
  </CardContent>
);

const DescriptionSection = () => {
  const record = useRecordContext<Record<string, any>>();
  if (!record?.description) return null;
  return (
    <CardContent>
      <CardHeader title="Description" sx={{ px: 0, pt: 0, pb: 1 }} />
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {record.description}
      </Typography>
    </CardContent>
  );
};

const ClicksCreativesTable = () => {
  const source = useCreativeReferenceSource();
  return (
  <CardContent>
    <CardHeader
      title="Clicks Creatives"
      action={<AddCreativeButton resource="clicksCreatives" label="Add clicks creative" />}
      sx={{ px: 0, pt: 0 }}
    />
    <ReferenceManyField
      reference="clicksCreatives"
      source={source}
      target="placementsLineItemId"
      sort={{ field: 'startsAt', order: 'DESC' }}
    >
      <Datagrid rowClick="edit" bulkActionButtons={false} empty={<Box sx={{ p: 2 }}>No clicks creatives.</Box>}>
        <TextField source="id" />
        <TextField source="name" label="Flight / Segment Name" />
        <TextField source="title" label="Creative Text" />
        <ESTDateField source="startsAt" label="Start Date" />
        <ESTDateField source="endsAt" label="End Date" />
        <FunctionField
          source="targetingType"
          label="Targeting"
          render={(record: Record<string, any>) => formatTargetingType(record?.targetingType)}
        />
        <TargetingDetail label="Targeting Details" />
        <FunctionField
          source="isApproved"
          label="Status"
          textAlign="center"
          render={(record: Record<string, any>) => <Chip isApproved={record?.isApproved} />}
        />
        <PreviewClickButton />
      </Datagrid>
    </ReferenceManyField>
  </CardContent>
  );
};

const FMACreativesTable = () => {
  const source = useCreativeReferenceSource();
  return (
  <CardContent>
    <CardHeader
      title="FMA Creatives"
      action={<AddCreativeButton resource="fmaCreatives" label="Add FMA creative" />}
      sx={{ px: 0, pt: 0 }}
    />
    <ReferenceManyField
      reference="fmaCreatives"
      source={source}
      target="placementsLineItemId"
      sort={{ field: 'startsAt', order: 'DESC' }}
    >
      <Datagrid rowClick="show" bulkActionButtons={false} empty={<Box sx={{ p: 2 }}>No FMA creatives.</Box>}>
        <TextField source="id" />
        <TextField source="name" label="Flight / Segment Name" />
        <ESTDateField source="startsAt" label="Start Date" />
        <ESTDateField source="endsAt" label="End Date" />
        <FunctionField
          source="isApproved"
          label="Status"
          textAlign="center"
          render={(record: Record<string, any>) => <Chip isApproved={record?.isApproved} />}
        />
        <PreviewFMAButton />
      </Datagrid>
    </ReferenceManyField>
  </CardContent>
  );
};

const SponsoredContentTeasersTable = () => {
  const source = useCreativeReferenceSource();
  return (
  <CardContent>
    <CardHeader
      title="Sponsored Content Teasers"
      action={<AddCreativeButton resource="sponsoredContentTeasers" label="Add sponsored teaser" />}
      sx={{ px: 0, pt: 0 }}
    />
    <ReferenceManyField
      reference="sponsoredContentTeasers"
      source={source}
      target="placementsLineItemId"
      sort={{ field: 'startsAt', order: 'DESC' }}
    >
      <Datagrid rowClick="show" bulkActionButtons={false} empty={<Box sx={{ p: 2 }}>No sponsored teasers.</Box>}>
        <TextField source="id" />
        <TextField source="title" label="Title" />
        <TextField source="contentId" label="Content ID" />
        <ESTDateField source="startsAt" label="Start Date" />
        <ESTDateField source="endsAt" label="End Date" />
        <FunctionField
          source="targetingType"
          label="Targeting"
          render={(record: Record<string, any>) => formatTargetingType(record?.targetingType)}
        />
        <FunctionField
          source="isApproved"
          label="Status"
          textAlign="center"
          render={(record: Record<string, any>) => <Chip isApproved={record?.isApproved} />}
        />
      </Datagrid>
    </ReferenceManyField>
  </CardContent>
  );
};

const BannerTargetingsTable = () => {
  const source = useCreativeReferenceSource();
  return (
  <CardContent>
    <CardHeader
      title="Banner Targeting"
      action={<AddCreativeButton resource="bannerTargetings" label="Add banner targeting" />}
      sx={{ px: 0, pt: 0 }}
    />
    <ReferenceManyField
      reference="bannerTargetings"
      source={source}
      target="placementsLineItemId"
      sort={{ field: 'startsAt', order: 'DESC' }}
    >
      <Datagrid rowClick="show" bulkActionButtons={false} empty={<Box sx={{ p: 2 }}>No banner targetings.</Box>}>
        <TextField source="id" />
        <TextField source="targetListId" label="Target List ID" />
        <ReferenceField label="Target List Name" reference="targetLists" source="targetListId">
          <TextField source="name" />
        </ReferenceField>
        <ESTDateField source="startsAt" label="Start Date" />
        <ESTDateField source="endsAt" label="End Date" />
      </Datagrid>
    </ReferenceManyField>
  </CardContent>
  );
};

export const LineItemCreativeSections = () => (
  <>
    <Divider />
    <ClicksCreativesTable />
    <Divider />
    <FMACreativesTable />
    <Divider />
    <SponsoredContentTeasersTable />
    <Divider />
    <BannerTargetingsTable />
  </>
);

export const LineItemShow = () => (
  <Show actions={<LineItemShowActions />}>
    <LineItemTitle />
    <Divider />
    <IdentitySection />
    <Divider />
    <ScheduleSection />
    <Divider />
    <FinancialSection />
    <Divider />
    <TargetingSection />
    <Divider />
    <ReferencesSection />
    <Divider />
    <ExclusionLabelsSection />
    <Divider />
    <DescriptionSection />
    <LineItemCreativeSections />
  </Show>
);
